import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMatches, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { format, parseISO } from 'date-fns'
import {
  Container,
  FormDivider,
  PageSubtitle,
  PageTitle,
  TextHeaderBox,
  PageHeaderBox,
  PageOptions,
  SectionHeaderRow,
  ContainerButtons,
  ContainerCard,
  DetailCard,
  IconDetailContainer,
  LabelCard,
  CardTitle,
  ReviewsList,
  ReviewItem,
  ReviewContent,
  ReviewHeader,
  ReviewClientName,
  ReviewRating,
  ReviewMeta,
  ReviewComment,
  ReviewLink,
  ModeratedBadge,
  ApprovedBadge,
  ReviewActions,
  EmptyState,
  ExceptionContainer,
  CloseButton,
  ModalContainerTop,
  ModalContainerBottom,
  ModalTitle,
  ModalSubText,
  ReasonTextArea,
  ReasonError,
  ListToolbar,
  FilterGroup,
  FilterChip,
  SortGroup,
  SortLabel,
  SortSelect,
} from './styles'

import { DashboardhMediumButton, Icon, Modal } from 'bd-web-components'
import {
  PRIMARY_RED_01,
  SECONDARY_BLUE_02,
  SECONDARY_GREEN_07,
  SECONDARY_YELLOW_05,
} from 'bd-web-components/src/utils/dash-colors'

import {
  EditEventReviewAction,
  EventReview,
  EventReviewStatistics,
  useReviews,
} from '../../hooks/reviews'
import Loader from '../../components/Loader'
import { useGuard } from '../../hooks/auth'
import { Breadcrumb } from '../../components/Breadcrumb'
import { closeButtonIcon } from '../../components/Icon/iconsImport'
import { IconProducer } from '../../components/Icon'
import { getDashColor } from '../../styles'
import { ExtraQuestionSection } from './ExtraQuestionSection'

const emptyStatistics: EventReviewStatistics = {
  answered: 0,
  percentage: 0,
  total: 0,
  avg: 0,
}

type ReviewFilter =
  | 'all'
  | 'pending'
  | 'approved'
  | 'moderated'
  | 'with_comment'

type ReviewSort = 'newest' | 'rating_high' | 'rating_low'

const FILTER_OPTIONS: { key: ReviewFilter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'pending', label: 'Não aprovados' },
  { key: 'approved', label: 'Aprovados' },
  { key: 'moderated', label: 'Moderados' },
  { key: 'with_comment', label: 'Com comentário' },
]

const SORT_OPTIONS: { key: ReviewSort; label: string }[] = [
  { key: 'newest', label: 'Mais recente' },
  { key: 'rating_high', label: 'Nota mais alta' },
  { key: 'rating_low', label: 'Nota mais baixa' },
]

function formatDate(value: string | null) {
  if (!value) return '-'
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm')
  } catch {
    return value
  }
}

function isPendingApproval(review: EventReview) {
  return review.approvedAt == null && review.moderated === false
}

function canApprove(review: EventReview) {
  return isPendingApproval(review)
}

function canExclude(review: EventReview) {
  return (
    review.moderated === false
  )
}

function renderStars(rating: number | null) {
  if (rating == null) return 'Sem nota'
  return `${'★'.repeat(rating)}${'☆'.repeat(Math.max(0, 5 - rating))} (${rating})`
}

function matchesFilter(review: EventReview, filter: ReviewFilter) {
  switch (filter) {
    case 'pending':
      return isPendingApproval(review)
    case 'approved':
      return Boolean(review.approvedAt)
    case 'moderated':
      return review.moderated
    case 'with_comment':
      return Boolean(review.userComment)
    case 'all':
    default:
      return true
  }
}

function sortReviews(list: EventReview[], sort: ReviewSort) {
  const sorted = [...list]

  switch (sort) {
    case 'rating_high':
      return sorted.sort(
        (a, b) => (b.userRating ?? -1) - (a.userRating ?? -1)
      )
    case 'rating_low':
      return sorted.sort(
        (a, b) => (a.userRating ?? Number.MAX_SAFE_INTEGER) - (b.userRating ?? Number.MAX_SAFE_INTEGER)
      )
    case 'newest':
    default:
      return sorted.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
  }
}

export function Reviews() {
  useGuard(['DP22'])

  const { id } = useParams<{ id: string }>()
  const {
    getEventReviews,
    editEventReview,
    approveAllEventReviews,
    getEventExtraQuestion,
    saveEventExtraQuestion,
    setEventExtraQuestionActive,
  } = useReviews(id)

  const [reviews, setReviews] = useState<EventReview[]>([])
  const [statistics, setStatistics] =
    useState<EventReviewStatistics>(emptyStatistics)
  const [isLoading, setIsLoading] = useState(true)
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
  const [excludeReview, setExcludeReview] = useState<EventReview | null>(null)
  const [moderatedReason, setModeratedReason] = useState('')
  const [reasonError, setReasonError] = useState(false)
  const [showApproveAllModal, setShowApproveAllModal] = useState(false)
  const [isApprovingAll, setIsApprovingAll] = useState(false)
  const [filter, setFilter] = useState<ReviewFilter>('all')
  const [sort, setSort] = useState<ReviewSort>('newest')

  const matches = useMatches()
  const crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) => ({
      name: match.handle.crumb(match.data),
      path: match.pathname,
    }))

  const pendingCount = useMemo(
    () => reviews.filter(isPendingApproval).length,
    [reviews]
  )

  const visibleReviews = useMemo(
    () =>
      sortReviews(
        reviews.filter((review) => matchesFilter(review, filter)),
        sort
      ),
    [reviews, filter, sort]
  )

  const loadReviews = useCallback(async () => {
    if (!id) return

    try {
      setIsLoading(true)
      const result = await getEventReviews()
      setReviews(result?.data ?? [])
      setStatistics(result?.statistics ?? emptyStatistics)
    } catch (error) {
      toast.error('Um erro aconteceu ao carregar as avaliações')
      setReviews([])
      setStatistics(emptyStatistics)
    } finally {
      setIsLoading(false)
    }
  }, [getEventReviews, id])

  useEffect(() => {
    loadReviews()
  }, [loadReviews])

  const handleEdit = useCallback(
    async (
      review: EventReview,
      action: EditEventReviewAction,
      reason?: string
    ) => {
      try {
        setActionLoadingId(review.idEventReview)
        await editEventReview(review.idEventReview, {
          action,
          ...(action === 'rejected' ? { moderatedReason: reason } : {}),
        })
        toast.success(
          action === 'rejected'
            ? 'Comentário excluído com sucesso'
            : 'Avaliação atualizada com sucesso'
        )
        setExcludeReview(null)
        setModeratedReason('')
        setReasonError(false)
        await loadReviews()
      } catch (error) {
        toast.error('Um erro aconteceu, tente novamente')
      } finally {
        setActionLoadingId(null)
      }
    },
    [editEventReview, loadReviews]
  )

  const handleConfirmExclude = async () => {
    if (!excludeReview) return

    const reason = moderatedReason.trim()
    if (!reason) {
      setReasonError(true)
      return
    }

    await handleEdit(excludeReview, 'rejected', reason)
  }

  const closeExcludeModal = () => {
    setExcludeReview(null)
    setModeratedReason('')
    setReasonError(false)
  }

  const handleApproveAll = async () => {
    try {
      setIsApprovingAll(true)
      await approveAllEventReviews()
      toast.success('Avaliações pendentes aprovadas com sucesso')
      setShowApproveAllModal(false)
      await loadReviews()
    } catch (error) {
      toast.error('Um erro aconteceu ao aprovar as avaliações')
    } finally {
      setIsApprovingAll(false)
    }
  }

  return (
    <>
      <Container>
        <Breadcrumb
          paths={[
            { name: 'Início', path: '/events' },
            { name: 'Evento', path: `/events/${id}` },
            ...crumbs,
          ]}
        />
        <PageHeaderBox>
          <TextHeaderBox>
            <PageTitle>Avaliações</PageTitle>
            <PageSubtitle>
              Acompanhe as avaliações enviadas pelos clientes que foram ao
              evento.
            </PageSubtitle>
          </TextHeaderBox>
        </PageHeaderBox>
        <FormDivider />

        <ExtraQuestionSection
          eventId={id}
          getEventExtraQuestion={getEventExtraQuestion}
          saveEventExtraQuestion={saveEventExtraQuestion}
          setEventExtraQuestionActive={setEventExtraQuestionActive}
        />

        <PageOptions>Relatório</PageOptions>
        <ContainerButtons>
          <ContainerCard>
            <DetailCard>
              <IconDetailContainer>
                <div>
                  <Icon
                    fill={PRIMARY_RED_01}
                    classes="question-circle-help-center"
                    iconName="userProfile"
                    width={22}
                    height={22}
                  />
                </div>
              </IconDetailContainer>
              <LabelCard>
                {isLoading && <Loader small noMargin />}
                {!isLoading && (
                  <>
                    Total de convites
                    <CardTitle>{statistics.total ?? 0}</CardTitle>
                  </>
                )}
              </LabelCard>
            </DetailCard>
          </ContainerCard>
          <ContainerCard>
            <DetailCard>
              <IconDetailContainer className="answered">
                <div>
                  <Icon
                    fill={SECONDARY_GREEN_07}
                    classes="question-circle-help-center"
                    iconName="report"
                    width={20}
                    height={20}
                  />
                </div>
              </IconDetailContainer>
              <LabelCard>
                {isLoading && <Loader small noMargin />}
                {!isLoading && (
                  <>
                    Avaliações respondidas
                    <CardTitle>{statistics.answered ?? 0}</CardTitle>
                  </>
                )}
              </LabelCard>
            </DetailCard>
          </ContainerCard>
          <ContainerCard>
            <DetailCard>
              <IconDetailContainer className="graph">
                <div>
                  <Icon
                    fill={SECONDARY_BLUE_02}
                    classes="question-circle-help-center"
                    iconName="businessBarGraph"
                    width={20}
                    height={20}
                  />
                </div>
              </IconDetailContainer>
              <LabelCard>
                {isLoading && <Loader small noMargin />}
                {!isLoading && (
                  <>
                    % de respostas
                    <CardTitle>{statistics.percentage ?? 0}%</CardTitle>
                  </>
                )}
              </LabelCard>
            </DetailCard>
          </ContainerCard>
          <ContainerCard>
            <DetailCard>
              <IconDetailContainer className="avg">
                <div>
                  <Icon
                    fill={SECONDARY_YELLOW_05}
                    classes="question-circle-help-center"
                    iconName="goldBar"
                    width={20}
                    height={20}
                  />
                </div>
              </IconDetailContainer>
              <LabelCard>
                {isLoading && <Loader small noMargin />}
                {!isLoading && (
                  <>
                    Média das notas
                    <CardTitle>{statistics.avg ?? 0}</CardTitle>
                  </>
                )}
              </LabelCard>
            </DetailCard>
          </ContainerCard>
        </ContainerButtons>

        <SectionHeaderRow>
          <PageOptions>Lista de avaliações</PageOptions>
          <DashboardhMediumButton
            onClick={() => setShowApproveAllModal(true)}
            color="#fff"
            background={getDashColor('SECONDARY_GREEN_07', 'GRAY_DARK')}
            height="48px"
            maxWidth="100%"
            variant={pendingCount === 0 || isLoading ? 'disabled' : 'contained'}
            width="220px"
            borderRadius="100px"
            disabled={pendingCount === 0 || isLoading || isApprovingAll}
          >
            Aprovar todas
          </DashboardhMediumButton>
        </SectionHeaderRow>
        <ListToolbar>
          <FilterGroup>
            {FILTER_OPTIONS.map((option) => (
              <FilterChip
                key={option.key}
                type="button"
                $active={filter === option.key}
                onClick={() => setFilter(option.key)}
              >
                {option.label}
              </FilterChip>
            ))}
          </FilterGroup>
          <SortGroup>
            <SortLabel>Ordenar por</SortLabel>
            <SortSelect
              value={sort}
              onChange={(event) => setSort(event.target.value as ReviewSort)}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </SortSelect>
          </SortGroup>
        </ListToolbar>

        {isLoading && (
          <ExceptionContainer height={240}>
            <Loader />
          </ExceptionContainer>
        )}
        {!isLoading && reviews.length === 0 && (
          <EmptyState>Nenhuma avaliação encontrada para este evento.</EmptyState>
        )}
        {!isLoading && reviews.length > 0 && visibleReviews.length === 0 && (
          <EmptyState>Nenhuma avaliação encontrada com esse filtro.</EmptyState>
        )}
        {!isLoading && visibleReviews.length > 0 && (
          <ReviewsList>
            {visibleReviews.map((review) => {
              const loading = actionLoadingId === review.idEventReview

              return (
                <ReviewItem key={review.idEventReview}>
                  <ReviewContent>
                    <ReviewHeader>
                      <ReviewClientName>{review.clientName}</ReviewClientName>
                      <ReviewRating>
                        {renderStars(review.userRating)}
                      </ReviewRating>
                      {review.moderated && (
                        <ModeratedBadge>Moderado</ModeratedBadge>
                      )}
                      {!review.moderated && review.approvedAt && (
                        <ApprovedBadge>Aprovado</ApprovedBadge>
                      )}
                    </ReviewHeader>
                    <ReviewMeta>
                      Avaliado em {formatDate(review.userRatingAt)} · Criado em{' '}
                      {formatDate(review.createdAt)}
                    </ReviewMeta>
                    {review.userComment ? (
                      <ReviewComment>{review.userComment}</ReviewComment>
                    ) : (
                      <ReviewComment>Sem comentário</ReviewComment>
                    )}
                    {review.approvedAt && (
                      <ReviewMeta>
                        Aprovado em {formatDate(review.approvedAt)}
                      </ReviewMeta>
                    )}
                    {review.moderated && review.moderatedReason && (
                      <ReviewMeta>
                        Motivo da exclusão: {review.moderatedReason}
                      </ReviewMeta>
                    )}
                    {review.url && (
                      <ReviewLink
                        href={review.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir link da avaliação
                      </ReviewLink>
                    )}
                  </ReviewContent>
                  <ReviewActions>
                    {canApprove(review) && (
                      <DashboardhMediumButton
                        onClick={() => handleEdit(review, 'approved')}
                        color="#fff"
                        background={getDashColor(
                          'SECONDARY_GREEN_07',
                          'GRAY_DARK'
                        )}
                        height="40px"
                        maxWidth="100%"
                        variant={loading ? 'disabled' : 'contained'}
                        width="120px"
                        borderRadius="100px"
                        disabled={loading}
                      >
                        {loading ? '...' : 'Aprovar'}
                      </DashboardhMediumButton>
                    )}
                    {review.moderated && (
                      <DashboardhMediumButton
                        onClick={() => handleEdit(review, 'approved')}
                        color="#fff"
                        background={getDashColor(
                          'SECONDARY_BLUE_01',
                          'GRAY_DARK'
                        )}
                        height="40px"
                        maxWidth="100%"
                        variant={loading ? 'disabled' : 'contained'}
                        width="180px"
                        borderRadius="100px"
                        disabled={loading}
                      >
                        {loading ? '...' : 'Restaurar comentário'}
                      </DashboardhMediumButton>
                    )}
                    {canExclude(review) && (
                      <DashboardhMediumButton
                        onClick={() => {
                          setExcludeReview(review)
                          setModeratedReason('')
                          setReasonError(false)
                        }}
                        color="#fff"
                        background={getDashColor('PRIMARY_RED_01', 'GRAY_DARK')}
                        height="40px"
                        maxWidth="100%"
                        variant={loading ? 'disabled' : 'contained'}
                        width="120px"
                        borderRadius="100px"
                        disabled={loading}
                      >
                        Excluir
                      </DashboardhMediumButton>
                    )}
                  </ReviewActions>
                </ReviewItem>
              )
            })}
          </ReviewsList>
        )}
      </Container>

      <Modal isShowing={Boolean(excludeReview)}>
        <ModalContainerTop>
          <div>
            <ModalTitle>Excluir comentário</ModalTitle>
            <ModalSubText>
              Informe o motivo da exclusão. Esse campo é obrigatório.
            </ModalSubText>
          </div>
          <div>
            <CloseButton onClick={closeExcludeModal} type="button">
              <IconProducer src={closeButtonIcon} />
            </CloseButton>
          </div>
        </ModalContainerTop>
        <ReasonTextArea
          value={moderatedReason}
          onChange={(event) => {
            setModeratedReason(event.target.value)
            if (event.target.value.trim()) setReasonError(false)
          }}
          placeholder="Descreva o motivo da exclusão"
          maxLength={500}
        />
        {reasonError && (
          <ReasonError>O motivo da exclusão é obrigatório.</ReasonError>
        )}
        <ModalContainerBottom>
          <DashboardhMediumButton
            onClick={closeExcludeModal}
            height="52px"
            maxWidth="100%"
            variant="outlined"
            width="133px"
          >
            Cancelar
          </DashboardhMediumButton>
          <DashboardhMediumButton
            onClick={handleConfirmExclude}
            disabled={actionLoadingId === excludeReview?.idEventReview}
            color="#fff"
            height="52px"
            maxWidth="100%"
            variant={
              actionLoadingId === excludeReview?.idEventReview
                ? 'disabled'
                : 'contained'
            }
            width="100%"
            background={getDashColor('PRIMARY_RED_01', 'GRAY_DARK')}
          >
            {actionLoadingId === excludeReview?.idEventReview ? (
              <Loader small={true} />
            ) : (
              'Confirmar'
            )}
          </DashboardhMediumButton>
        </ModalContainerBottom>
      </Modal>

      <Modal isShowing={showApproveAllModal}>
        <ModalContainerTop>
          <div>
            <ModalTitle>Aprovar todas as avaliações</ModalTitle>
            <ModalSubText>
              Todos os reviews ainda não aprovados serão aprovados e passarão a
              aparecer na página do evento. Deseja continuar?
            </ModalSubText>
          </div>
          <div>
            <CloseButton
              onClick={() => setShowApproveAllModal(false)}
              type="button"
              disabled={isApprovingAll}
            >
              <IconProducer src={closeButtonIcon} />
            </CloseButton>
          </div>
        </ModalContainerTop>
        <ModalContainerBottom>
          <DashboardhMediumButton
            onClick={() => setShowApproveAllModal(false)}
            height="52px"
            maxWidth="100%"
            variant="outlined"
            width="133px"
            disabled={isApprovingAll}
          >
            Cancelar
          </DashboardhMediumButton>
          <DashboardhMediumButton
            onClick={handleApproveAll}
            disabled={isApprovingAll}
            color="#fff"
            height="52px"
            maxWidth="100%"
            variant={isApprovingAll ? 'disabled' : 'contained'}
            width="100%"
            background={getDashColor('SECONDARY_GREEN_07', 'GRAY_DARK')}
          >
            {isApprovingAll ? <Loader small={true} /> : 'Confirmar'}
          </DashboardhMediumButton>
        </ModalContainerBottom>
      </Modal>
    </>
  )
}
